import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RotateCcw, LayoutDashboard } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { ujmsV2DashCatalog, ujmsV2DashLayout, ujmsV2SaveDashLayout, ujmsV2ResetDashLayout } from "../../services/ujmsApi";
import { cls } from "../ui";
import { Widget } from "./DashboardWidget";

export default function Dashboard() {
  const { access } = useOutletContext();
  const [catalog, setCatalog] = useState([]);
  const [widgets, setWidgets] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const [maxKey, setMaxKey] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    Promise.all([ujmsV2DashCatalog(), ujmsV2DashLayout()]).then(([c, l]) => { setCatalog(c.data); setWidgets(l.data.widgets); }).catch(() => { setCatalog([]); setWidgets([]); });
  }, []);

  const persist = useCallback((next) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => ujmsV2SaveDashLayout(next).catch(() => toast.error("Layout not saved")), 600);
  }, []);
  const update = (fn) => setWidgets((prev) => { const next = fn(prev); persist(next); return next; });

  const add = (key) => { const spec = catalog.find((c) => c.key === key); update((p) => [...p, { key, w: spec.w, h: spec.h, threshold: spec.threshold }]); toast.success(`${spec.title} added`); };
  const remove = (key) => update((p) => p.filter((w) => w.key !== key));
  const patch = (key, changes) => update((p) => p.map((w) => (w.key === key ? { ...w, ...changes } : w)));
  const reorder = (from, to) => { if (from === to) return; update((p) => { const arr = [...p]; const fi = arr.findIndex((w) => w.key === from); const [m] = arr.splice(fi, 1); const ti = arr.findIndex((w) => w.key === to); arr.splice(ti, 0, m); return arr; }); };
  const reset = async () => { try { const r = await ujmsV2ResetDashLayout(); setWidgets(r.data.widgets); setMaxKey(null); toast.success("Layout reset to role default"); } catch { toast.error("Reset failed"); } };

  const available = catalog.filter((c) => !(widgets || []).some((w) => w.key === c.key));
  const shown = maxKey ? (widgets || []).filter((w) => w.key === maxKey) : widgets || [];
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="v2-dashboard">
      <div className="ent-edition-hdr mb-1.5">
        <LayoutDashboard className="w-4 h-4 text-ujms-dark" /><h1 className="ent-edition-title">Dashboard</h1>
        <span className="text-[11px] text-slate-500" data-testid="dash-widget-count">{widgets ? `${widgets.length} widget(s)` : "Loading…"}</span>
        <div className="ml-auto flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1 border border-slate-300 bg-white text-[11px] px-2 py-px rounded-sm hover:bg-slate-50 disabled:opacity-50" disabled={!available.length} data-testid="dash-add-widget"><Plus className="w-3 h-3" /> Add Widget</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[280px] rounded-none text-[12px] p-0.5" data-testid="dash-add-menu">
              {available.map((c) => (
                <DropdownMenuItem key={c.key} onSelect={() => add(c.key)} className="rounded-none flex-col items-start py-1 cursor-pointer" data-testid={`dash-add-${c.key}`}>
                  <span className="font-semibold">{c.title}</span><span className="text-[10px] text-slate-500">{c.description}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button className="inline-flex items-center gap-1 border border-slate-300 bg-white text-[11px] px-2 py-px rounded-sm hover:bg-slate-50" onClick={reset} data-testid="dash-reset"><RotateCcw className="w-3 h-3" /> Reset</button>
        </div>
      </div>
      {widgets && !widgets.length && <div className="text-[12px] text-slate-500 italic p-6 text-center border border-dashed border-slate-300 bg-white" data-testid="dash-empty">Your dashboard is empty — use Add Widget to build it.</div>}
      <div className={cls("grid gap-3 flex-1 min-h-0 content-start", maxKey ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-2")} data-testid="dash-grid">
        {shown.map((w) => (
          <div key={w.key} className={cls("min-w-0", !maxKey && w.w >= 2 && "xl:col-span-2", overKey === w.key && dragKey !== w.key && "ring-2 ring-ujms ring-offset-1")}
            onDragOver={(e) => { e.preventDefault(); setOverKey(w.key); }} onDrop={() => { if (dragKey) reorder(dragKey, w.key); setDragKey(null); setOverKey(null); }} data-testid={`dash-slot-${w.key}`}>
            <Widget pos={maxKey ? { ...w, h: Math.max(w.h, window.innerHeight - 120) } : w} spec={catalog.find((c) => c.key === w.key)} access={access} onRemove={() => remove(w.key)} onPatch={(changes) => patch(w.key, changes)}
              maximized={maxKey === w.key} onMaximize={() => setMaxKey(maxKey === w.key ? null : w.key)}
              onDragStart={() => setDragKey(w.key)} onDragEnd={() => { setDragKey(null); setOverKey(null); }} dragging={dragKey === w.key} />
          </div>
        ))}
      </div>
    </div>
  );
}
